/**
 * 区域状态管理 Store
 * 使用 Zustand 管理全局区域配置和合规状态
 *
 * 功能：
 * - 存储 Bootstrap API 返回的区域配置
 * - 提供 computed getters（isGDPR, isCCPA, isPIPL, isStandard）
 * - 使用 persist 中间件持久化到 localStorage
 *
 * **Validates: Requirements 4.5**
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { BootstrapData } from "@/hooks/useBootstrap";
import type { ComplianceMode } from "@/types/operating-region";

/**
 * 区域状态接口
 */
interface RegionState {
  // 状态
  bootstrapData: BootstrapData | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setBootstrapData: (data: BootstrapData) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

/**
 * 初始状态
 */
const initialState = {
  bootstrapData: null as BootstrapData | null,
  isLoading: true,
  error: null as string | null,
};

/**
 * 区域状态 Store
 *
 * 功能：
 * - 管理 Bootstrap API 返回的区域配置
 * - 提供合规模式判断方法
 * - 持久化部分状态到 localStorage
 */
export const useRegionStore = create<RegionState>()(
  persist(
    (set) => ({
      ...initialState,

      setBootstrapData: (data) =>
        set({
          bootstrapData: data,
          isLoading: false,
          error: null,
        }),

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) =>
        set({
          error,
          isLoading: false,
        }),

      reset: () => set(initialState),
    }),
    {
      name: "region-store",
      storage: createJSONStorage(() => localStorage),
      // 只持久化 bootstrapData
      partialize: (state) => ({
        bootstrapData: state.bootstrapData,
      }),
    }
  )
);

// ============================================
// Computed Selectors (便捷的选择器 hooks)
// ============================================

/**
 * 获取当前区域代码
 */
export const useRegion = () =>
  useRegionStore((state) => state.bootstrapData?.detected_region || "GLOBAL");

/**
 * 获取当前合规模式
 */
export const useComplianceMode = (): ComplianceMode =>
  useRegionStore((state) => state.bootstrapData?.compliance_mode || "standard");

/**
 * 判断是否为 GDPR 模式（欧盟）
 */
export const useIsGDPR = () =>
  useRegionStore((state) => state.bootstrapData?.compliance_mode === "gdpr");

/**
 * 判断是否为 CCPA 模式（加州）
 */
export const useIsCCPA = () =>
  useRegionStore((state) => state.bootstrapData?.compliance_mode === "ccpa");

/**
 * 判断是否为 PIPL 模式（中国）
 */
export const useIsPIPL = () =>
  useRegionStore((state) => state.bootstrapData?.compliance_mode === "pipl");

/**
 * 判断是否为 Standard 模式（通用）
 */
export const useIsStandard = () =>
  useRegionStore(
    (state) => state.bootstrapData?.compliance_mode === "standard"
  );

/**
 * 获取 Bootstrap 数据
 */
export const useBootstrapData = () =>
  useRegionStore((state) => state.bootstrapData);

/**
 * 获取加载状态
 */
export const useRegionLoading = () =>
  useRegionStore((state) => state.isLoading);

/**
 * 获取错误信息
 */
export const useRegionError = () => useRegionStore((state) => state.error);

/**
 * 空功能开关对象（避免每次返回新引用导致无限循环）
 */
const EMPTY_FEATURES: Record<string, boolean> = {};

/**
 * 获取功能开关
 */
export const useRegionFeatures = () =>
  useRegionStore((state) => state.bootstrapData?.features ?? EMPTY_FEATURES);

/**
 * 获取协议链接
 */
export const useRegionLinks = () =>
  useRegionStore((state) => state.bootstrapData?.links);

/**
 * 获取法律实体
 */
export const useLegalEntity = () =>
  useRegionStore((state) => state.bootstrapData?.legal_entity);

/**
 * 获取默认语言
 */
export const useDefaultLanguage = () =>
  useRegionStore((state) => state.bootstrapData?.default_language);

export default useRegionStore;
